import { useState, useMemo } from 'react';
import CategorySection from './CategorySection.jsx';
import CCFilterBar from './CCFilterBar.jsx';
import { cn } from '@/lib/utils.js';
import { formatTimeOfDay } from '@/lib/date-display.js';

// Command Center — deal STATE by kind-of-work (distinct from Traffic Pattern's
// urgency feed). Page = sticky day header + red/amber/tracking KPI band over
// collapsible kind-of-work sections (design-reference/command-center.mockup.html).

function KpiPill({ dot, num, label }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5">
      <span className={cn('h-2 w-2 rounded-full', dot)} />
      <span className="text-sm font-bold text-text">{num || 0}</span>
      <span className="text-[11px] text-text-dim">{label}</span>
    </div>
  );
}

export default function CommandCenterBoard({ data }) {
  const sections = data?.sections || [];
  const counts = data?.counts || { urgent: 0, warn: 0, info: 0 };
  const [filters, setFilters] = useState({ stages: [], carriers: [] });

  const now = new Date();
  const dayName = now.toLocaleDateString('en-US', { weekday: 'long' });
  const fullDate = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const filtered = useMemo(() => {
    const hasStage = filters.stages?.length > 0;
    const hasCarrier = filters.carriers?.length > 0;
    if (!hasStage && !hasCarrier) return sections;
    return sections.map((s) => {
      const items = (s.items || []).filter((item) => {
        if (hasStage && !filters.stages.includes(item.stage)) return false;
        if (hasCarrier) {
          const itemCarriers = (item.carriers || []).map((c) => c.carrier_name);
          if (!filters.carriers.some((f) => itemCarriers.includes(f))) return false;
        }
        return true;
      });
      return { ...s, items };
    });
  }, [sections, filters]);

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6">
      {/* Sticky day header + KPI band (red / amber / tracking — no blue) */}
      <header className="sticky top-0 z-10 -mx-4 mb-5 flex items-end gap-6 border-b border-border bg-bg/95 px-4 pb-3 pt-1 backdrop-blur md:-mx-6 md:px-6">
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-text-dim">{dayName}</div>
          <div className="text-xl font-semibold tracking-tight text-text">{fullDate}</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <KpiPill dot="bg-accent" num={counts.urgent} label="red" />
          <KpiPill dot="bg-warning" num={counts.warn} label="amber" />
          <KpiPill dot="bg-success" num={counts.info} label="tracking" />
        </div>
      </header>

      <div className="mb-4">
        <CCFilterBar sections={sections} filters={filters} onFiltersChange={setFilters} />
      </div>

      <div className="space-y-5">
        {filtered.map((section) => (
          <CategorySection key={section.key} section={section} />
        ))}
      </div>

      {data?.asOf ? (
        <p className="pt-4 text-center font-mono text-[10px] text-text-dim">
          Refreshed {formatTimeOfDay(data.asOf)}
        </p>
      ) : null}
    </div>
  );
}
